import { ConstructorType, inject, injectValue } from '../../../../../../common/container/inject';
import { IError } from '../../../../../../common/error/IError';
import { DomainStore } from '../../../../../../common/store/base/DomainStore';
import { IApplicationDataModel } from '../../../../../../service/application/data/IApplicationDataModel';
import { IApplicationModel } from '../../../../../../service/application/entity/IApplicationModel';
import { IProjectModel } from '../../../../../../service/project/IProjectModel';
import { IUserProjectRoleModel } from '../../../../../../service/projectRole/entity/IUserProjectRoleModel';
import { injectRootService } from '../../../../../../service/RootServiceFactory';
import { IRouterService, RouterServiceToken } from '../../../../../../service/route/IRouterService';
import { IStatusValueModel } from '../../../../../../service/status/value/IStatusValueModel';
import { UserSystemRoleModelPermissionMap } from '../../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { IUserEntityModel } from '../../../../../../service/user/IUserEntityModel';
import { LayoutNotificationType } from '../../../../../layout/common/notification/store/ILayoutNotification';
import { IMainLayoutDomainStore } from '../../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { IProjectView, defaultProjectView } from './model/IProjectView';
import { ProjectInfoDataMapper } from './model/ProjectInfoDataMapper';
import { ProjectInfoUI } from './ProjectInfoUI';

type JoiningAction = null | (() => void);
export class ProjectInfoDomain extends DomainStore {
  public joiningAction: JoiningAction = null;

  constructor(
    public layoutDomain: IMainLayoutDomainStore,
    private uiStore = new (injectValue<ConstructorType<ProjectInfoUI>>(ProjectInfoUI))(),
    private publicRootService = injectRootService(layoutDomain.serviceType.value),
    private router: IRouterService = inject<IRouterService>(RouterServiceToken),
    private dataMapper = new ProjectInfoDataMapper(),
  ) {
    super();
  }

  async loadData(projectId?: string): Promise<void> {
    this.uiStore.startLoading();

    const loadDataPromises = [this.getProjectData(projectId)];
    if (projectId) {
      loadDataPromises.push(this.getMyJoiningInfo(projectId))
    }

    await Promise.all(loadDataPromises);
    this.loadChatCounters()

    const isAdmin = this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]);
    if (isAdmin && projectId) {
      await this.fetchInvitationLinks(projectId)
    }

    this.uiStore.endLoading();
  }

  async getProjectData(projectId) {
    const projects = await this.publicRootService.project.entity
      .search({ limit: 100000, filter: { id: projectId } })
      .catch(this.dataLoadErrorCatch(this.errorDefaultSearchValue<IProjectModel>()));
    this.ui.notClearProjects.setList(projects.data)

    const projectIds = projects.data.map((project) => project.id as string) || [];

    const applicationsFilter = this.getApplicationsFilter(projectIds);
    const applicationsDataFilter = this.getApplicationsDataFilter(projectIds);

    const [
      applications,
      applicationsData,
      projectRoles,
      statusesValues
    ] = await Promise.all([
      this.publicRootService.application.entity.search({
        limit: 100000,
        filter: applicationsFilter,
        fields: ['id', 'name', 'performerTypeId', 'dataId', 'projectId', 'isUnitedWithProject'],
      }),
      this.publicRootService.application.data.search({
        limit: 100000,
        filter: applicationsDataFilter,
        fields: ['id', 'requirementCount', 'applicationId', 'projectId', 'statusesStatistic', 'statusesIds', 'statusesValuesIds'],
      }),
      this.publicRootService.projectRole.entity.search({ limit: 100000 }),
      this.publicRootService.status.value.search({ limit: 100000 }),
    ]);
    this.ui.applications.setList(applications.data)
    this.ui.applicationsData.setList(applicationsData.data)
    this.ui.projectRoles.setList(projectRoles.data)
    this.ui.allStatusesValues.setList(statusesValues.data);
    await this.loadProjects()

  }

  async loadChatCounters() {
    const projectIds = this.ui.notClearProjects.list.map((project) => project.id as string) || [];

    const projectNewMessagesCounterData = await this.publicRootService.project.entity.getProjectsApplicationsCounters({
      limit: 100000,
      filter: { ids: { in: projectIds } },
      fields: ['id', 'isActive', 'name'],
    })

    this.ui.projectsNewMessagesData.setValue(projectNewMessagesCounterData.data)

    this.loadProjects()
  }

  private async loadProjects(projectId?: string) {
    const applications = this.ui.applications.list || []
    const applicationsData = this.ui.applicationsData.list || []
    const projectRoles = this.ui.projectRoles.list || []
    const statusesValues = this.ui.allStatusesValues.list || []
    const projectNewMessagesCounterData = this.ui.projectsNewMessagesData.value || []
    const projects = this.ui.notClearProjects.list || []

    let projectViews = await Promise.all(
      projects.map((project) =>
        this.makeProjectView(project, projectNewMessagesCounterData, applications, applicationsData, project.projectUsers || [], projectRoles, statusesValues),
      ),
    );
    projectViews = this.sortEmptyProjectsToBottomOfList(projectViews);
    this.ui.projects.setList(projectViews);
    projects.length > 0 ? this.ui.projects.setList(projectViews) : this.ui.projects.setList([defaultProjectView]);
    this.setTeamsData(projectViews);
  }


  private getMyJoiningInfo = async (projectId: string) => this.publicRootService.invitationsLinks.getMyJoiningInProject(projectId).then(userJoining => {
    if (userJoining && userJoining.role.id) {
      this.ui.userJoininInfo.setEntity(userJoining)
      this.ui.userRoleByInvitation.setValue(userJoining?.role.id)
    }
  }).catch(() => this.ui.userJoininInfo.setEntity(null));

  private getApplicationsFilter(projectIds: string[]) {
    if (projectIds.length > 1) {
      if (
        this.layoutDomain.userHaveAnyAccess([
          UserSystemRoleModelPermissionMap['global-allow-any'],
          UserSystemRoleModelPermissionMap['project-not-in-team-access'],
        ])
      ) {
        return {};
      } else {
        return { projectId: { in: projectIds } };
      }
    } else {
      return { projectId: { equal: projectIds[0] } };
    }
  }

  private getApplicationsDataFilter(projectIds: string[]) {
    if (projectIds.length > 1) {
      if (
        this.layoutDomain.userHaveAnyAccess([
          UserSystemRoleModelPermissionMap['global-allow-any'],
          UserSystemRoleModelPermissionMap['project-not-in-team-access'],
        ])
      ) {
        return {};
      } else {
        return { projectId: { in: projectIds } };
      }
    } else {
      return { projectId: { equal: projectIds[0] } };
    }
  }

  private setTeamsData(projectViews: IProjectView[]) {
    const result: {
      projectId: string;
      data: { memberId: string; data: { style?: any; content: any }[] }[];
    }[] = [];
    projectViews.forEach((view) => {
      result.push({
        projectId: view.id,
        data: view.team.map((member) => ({
          memberId: member.id,
          data: [
            { content: `${member.fullName} (${member.login})` },
            { width: 40, content: '' },
            { content: member.roleName },
          ],
        })),
      });
    });

    this.ui.teamTableRowsContent.setList(result);
  }

  private sortEmptyProjectsToBottomOfList(projectViews: IProjectView[]): IProjectView[] {
    return projectViews.sort((projectView) => (projectView.applications.length < 0 ? 1 : -1));
  }

  private makeProjectView = async (
    project: IProjectModel,
    projectNewMessagesCounterData: {
      applicationId: string;
      newMessages: number;
      projectId: string;
    }[],
    allApplications: IApplicationModel[],
    allApplicationsData: IApplicationDataModel[],
    projectUsers: IUserEntityModel[],
    allProjectRoles: IUserProjectRoleModel[],
    allStatusesValues: IStatusValueModel[],
  ): Promise<IProjectView> => {
    const usersIds = project?.rolesMap?.data?.map((item) => item.userId) || [];
    const projectView = this.dataMapper.mapProject(
      {
        project,
        projectApplicationsData: allApplicationsData.filter(
          (applicationData) => applicationData.projectId === project?.id,
        ),
        projectApplications: allApplications.filter((application) => application.projectId === project?.id),
        projectUsers: projectUsers,
        projectRolesList: allProjectRoles,
        activeUser: this.layoutDomain.ui.activeUser.entity,
        layoutDomain: this.layoutDomain,
      },
      {
        allStatusesValues: allStatusesValues,
      },
      projectNewMessagesCounterData,
    );
    return projectView;
  };

  private errorDefaultSearchValue<ValueType>(): { data: ValueType[] } {
    return { data: [] };
  }

  private dataLoadErrorCatch = <DefaultType>(defaultValue: DefaultType) => {
    const handler = (error: IError): DefaultType => {
      this.layoutDomain.errors.handleError(error);
      return defaultValue;
    };
    return handler;
  };

  get ui() {
    return this.uiStore;
  }

  async transferApplicationToOtherProject() {
    this.uiStore.isTransferLoading.setValue(true);
    const projectId = this.uiStore.transferProjectId.value;
    const application = this.uiStore.transferApplication.entity;

    if (projectId && application) {
      await this.publicRootService.application.entity.transferApplication(application.id, projectId);
      await this.uiStore.isOpenTransferModal.setValue(false);
      this.router.goTo(`/project/${projectId}/info`);
      await this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.success,
        text: `Система ${application.name} перенесена`,
      });
    } else {
      await this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: `Ошибка валидации, выберите проект для переноса`,
      });
    }
    this.uiStore.isTransferLoading.setValue(false);
  }

  async copyApplicationToOtherProject() {
    this.uiStore.isTransferLoading.setValue(true);
    const projectId = this.uiStore.copyApplicationData.value.projectId;
    const application = this.uiStore.transferApplication.entity;
    const newApplicationName = this.uiStore.copyApplicationData.value.newApplicationName;
    const currentData = this.uiStore.copyApplicationData.value;

    if (projectId && application && newApplicationName) {
      try {
        currentData.applicationId = application.id;
        await this.publicRootService.application.entity.copyApplication(currentData);
        await this.uiStore.isOpenTransferModal.setValue(false);
        this.router.goTo(`/project/${projectId}/info`);
        await this.loadData(projectId);
        await this.layoutDomain.notifications.showNotification({
          type: LayoutNotificationType.success,
          text: `Система ${application.name} копирована`,
        });
        this.ui.transferApplicationType.setValue(null);
        this.ui.transferProjectId.setValue('');
        this.ui.transferApplication.setEntity(null);
        this.ui.copyApplicationData.setValue({
          newApplicationName: '',
          applicationId: '',
          projectId: '',
          newLabelsForJira: [],
          needToCopy: {
            isNeedLocalRequirements: false,
            isNeedStatuses: false,
            isNeedAdditionalColumns: false,
            isNeedJiraIntegrations: false,
            isNeedVerifyAllRequirements: false,
          },
        });
      } catch (e) {
        const error = e as IError;
        await this.layoutDomain.notifications.showNotification({
          type: LayoutNotificationType.error,

          text: error?.data[0]?.message || `Ошибка копирования, попробуйте снова.`,
        });
      }
    } else {
      await this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: `Ошибка валидации, проверьте заполненность полей`,
      });
    }
    this.uiStore.isTransferLoading.setValue(false);
  }

  async loadAllProjectsNamesAndId() {
    const projects = await this.publicRootService.project.entity
      .search({
        limit: 10000,
        fields: ['id', 'name'],
        filter: {
          isUnitedWithApplication: {
            equal: false,
          },
        },
      })
      .catch(this.dataLoadErrorCatch(this.errorDefaultSearchValue<IProjectModel>()));

    this.uiStore.projectNames.setList(projects.data);
  }

  async openTransferModalWindow(projectId) {
    this.uiStore.isOpenTransferModal.setValue(true);
    this.uiStore.transferProjectId.setValue(projectId);
  }

  isUserCanTransferApplication() {
    const isCanTransferApplication = this.layoutDomain.userHaveAnyAccess([
      UserSystemRoleModelPermissionMap['settings-user-system-role-application-transfer'],
    ]);

    return isCanTransferApplication;
  }

  searchRequest(searchTerm) {
    this.ui.searchTerm.setValue(searchTerm);

    const searchRegex = new RegExp(this.ui.searchTerm.value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');

    const filteredRows = [...this.ui.projects.list].filter((row) => {
      return Object.keys(row).some((field) => {
        let haveSome = false;
        if (field === 'applications') {
          const currentProjectApplications = row[field];
          const filtredResult = currentProjectApplications.filter((application) => {
            return searchRegex.test(application.name);
          });
          haveSome = filtredResult.length > 0;
        }
        return haveSome;
      });
    });

    this.ui.filteredProjects.setList(filteredRows.length ? filteredRows : []);
  }

  async getCountOfProjectsAndApplications() {
    const count = await this.publicRootService.project.entity.getCount().catch(this.dataLoadErrorCatch);
    this.ui.projectsTotalCount.setValue(count?.projectCount || 0);
    this.ui.applicationsTotalCount.setValue(count?.applicationCount || 0);
  }

  async fetchInvitationLinks(projectId: string) {
    const res = await this.publicRootService.invitationsLinks.getListByProject(projectId);
    this.ui.invitationLinks.setList(res)
  }

  async createInvitationLink({ project, abledRoles }: { project: string; abledRoles: { id: string }[] }) {
    return await this.publicRootService.invitationsLinks.createByModel({ project, abledRoles });
  }

  async getInvitationLinkById(id: string) {
    return await this.publicRootService.invitationsLinks.getById(id);
  }

  async deleteInvitationLink({ id }: { id: string }) {
    return await this.publicRootService.invitationsLinks.deleteById(id);
  }
  // editing role by user

  public async loadDependecies(projectId: string): Promise<void> {
    const projectSearchResult = await this.publicRootService.project.entity.getById(projectId)
    this.ui.currentProject.setEntity(projectSearchResult)
  }




  public setUserRole(newRoleId: string | null,): void {
    this.ui.userRoleByInvitation.setValue(newRoleId);

    if (newRoleId === this.ui.userJoininInfo.entity?.role.id) {
      this.joiningAction = null;
    } else if (newRoleId && this.ui.userJoininInfo.entity && this.ui.userJoininInfo.entity.link !== null && this.ui.userJoininInfo.entity?.link.id) {
      const invitationId = this.ui.userJoininInfo.entity?.link.id
      this.joiningAction = () => this.publicRootService.invitationsLinks.joinByInvitation({ invitationId, roleId: newRoleId })
    }

  }

  public toggleLeftProject(): void {
    const prev = this.ui.hasUserLeftProject.value;
    if (this.joiningAction === this.leftFromProject) {
      this.joiningAction = null;
      this.ui.userRoleByInvitation.setValue(this.ui.userJoininInfo.entity?.role.id ?? null)
    } else {
      this.joiningAction = this.leftFromProject;
    }
    this.ui.hasUserLeftProject.setValue(!prev);
  }

  public leftFromProject() {
    if (this.ui.userJoininInfo.entity?.link.id) {
      this.publicRootService.invitationsLinks.removeByInvitation(this.ui.userJoininInfo.entity.link.id)
    }
  }

  public activateJoiningAction() {
    this.joiningAction?.()
    this.joiningAction = null;
  }

  userHaveAnyAccess(
    systemPermissions: string[] = [],
    projectPermissions: { id: string; permissions: string[] },
  ): boolean {
    let isAllowed: boolean | null = null;
    if (systemPermissions && systemPermissions.length > 0) {
      isAllowed =
        isAllowed ||
        this.ui.activeUser.entity.allowedPermissions.some((item) => systemPermissions.includes(item)) ||
        this.ui.activeUser.entity.allowedPermissions.includes(UserSystemRoleModelPermissionMap['global-allow-any']);
    }


    if (projectPermissions) {
      const project = this.ui.userProjects.list.find((project) => project.id === projectPermissions.id);
      const userRoleId = project?.rolesMap?.data?.find(
        (roleData) => roleData.userId === this.ui.activeUser.entity.id,
      )?.roleId;
      if (userRoleId) {
        const role = this.ui.projectRoles.list.find((role) => role.id === userRoleId);
        if (role) {
          const roleActions = role.permissions.data.map((item) => item.action);
          isAllowed = isAllowed || roleActions.some((action) => projectPermissions.permissions.includes(action));
        } else {
          isAllowed = isAllowed || false;
        }
      } else {
        isAllowed = isAllowed || false;
      }
    }
    return isAllowed ?? true;
  }
}
